import { useEffect, useMemo } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import Placeholder from "@tiptap/extension-placeholder";

import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Undo2,
  Redo2,
  Link2,
  Unlink2,
  Minus,
  Eraser,
} from "lucide-react";

type Props = {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  className?: string;
};

function IconButton({
  active,
  disabled,
  onClick,
  title,
  label,
  children,
}: {
  active?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  title?: string;
  label: string;
  children: React.ReactNode;
}) {
  const base =
    "inline-flex h-9 items-center gap-2 rounded-md border px-3 text-sm transition whitespace-nowrap";
  const normal =
    "border-slate-200 bg-white text-slate-700 hover:bg-slate-50";
  const act =
    "border-slate-900 bg-slate-900 text-white hover:bg-slate-800";
  const dis = "opacity-50 cursor-not-allowed hover:bg-white";

  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={disabled}
      className={[
        base,
        active ? act : normal,
        disabled ? dis : "",
      ].join(" ")}
    >
      {children}
      <span className="text-xs font-medium">{label}</span>
    </button>
  );
}

export default function TipTapEditor({
  value,
  onChange,
  placeholder = "내용을 입력하세요…",
  className = "",
}: Props) {
  const extensions = useMemo(
    () => [
      StarterKit.configure({
        heading: { levels: [2, 3] },
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
        HTMLAttributes: {
          rel: "noopener noreferrer",
          target: "_blank",
          class: "text-brand-600 underline underline-offset-2",
        },
      }),
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      Placeholder.configure({
        placeholder,
      }),
    ],
    [placeholder]
  );

  const editor = useEditor({
    extensions,
    content: value || "",
    editorProps: {
      attributes: {
        class:
          "min-h-[420px] px-5 py-4 focus:outline-none prose prose-slate max-w-none",
      },
    },
    onUpdate({ editor }) {
      onChange(editor.getHTML());
    },
  });

  // 외부에서 value가 바뀌는 경우(로드/새로고침) 에디터 내용 동기화
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const next = value || "";
    if (current !== next) {
      editor.commands.setContent(next, { emitUpdate: false });
    }
  }, [value, editor]);

  if (!editor) return null;

  const setLink = () => {
    const prev = editor.getAttributes("link").href as string | undefined;
    const url = window.prompt("링크 URL을 입력하세요", prev || "https://");
    if (url === null) return;

    const trimmed = url.trim();
    if (!trimmed) {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }

    editor
      .chain()
      .focus()
      .extendMarkRange("link")
      .setLink({ href: trimmed })
      .run();
  };

  return (
    <div className={["rounded-xl border border-slate-200 bg-white", className].join(" ")}>
      {/* ✅ 툴바: "두번째 이미지"처럼 툴바 느낌으로 정리 + sticky */}
      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-1 border-b border-slate-200 bg-white/95 px-2 py-2 backdrop-blur">
        <IconButton
          label="굵게"
          title="굵게"
          active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={!editor.can().chain().focus().toggleBold().run()}
        >
          <Bold size={18} />
        </IconButton>

        <IconButton
          label="기울임"
          title="기울임"
          active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          disabled={!editor.can().chain().focus().toggleItalic().run()}
        >
          <Italic size={18} />
        </IconButton>

        <IconButton
          label="밑줄"
          title="밑줄"
          active={editor.isActive("underline")}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        >
          <UnderlineIcon size={18} />
        </IconButton>

        <IconButton
          label="취소선"
          title="취소선"
          active={editor.isActive("strike")}
          onClick={() => editor.chain().focus().toggleStrike().run()}
        >
          <Strikethrough size={18} />
        </IconButton>

        <div className="mx-1 h-6 w-px bg-slate-200" />

        <IconButton
          label="제목2"
          title="H2"
          active={editor.isActive("heading", { level: 2 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        >
          <Heading2 size={18} />
        </IconButton>

        <IconButton
          label="제목3"
          title="H3"
          active={editor.isActive("heading", { level: 3 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        >
          <Heading3 size={18} />
        </IconButton>

        <div className="mx-1 h-6 w-px bg-slate-200" />

        <IconButton
          label="목록"
          title="불릿 목록"
          active={editor.isActive("bulletList")}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          <List size={18} />
        </IconButton>

        <IconButton
          label="번호"
          title="번호 목록"
          active={editor.isActive("orderedList")}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <ListOrdered size={18} />
        </IconButton>

        <IconButton
          label="인용"
          title="인용"
          active={editor.isActive("blockquote")}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
        >
          <Quote size={18} />
        </IconButton>

        <IconButton
          label="구분선"
          title="구분선"
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
        >
          <Minus size={18} />
        </IconButton>

        <div className="mx-1 h-6 w-px bg-slate-200" />

        <IconButton label="링크" title="링크" active={editor.isActive("link")} onClick={setLink}>
          <Link2 size={18} />
        </IconButton>

        <IconButton
          label="링크해제"
          title="링크 제거"
          onClick={() => editor.chain().focus().unsetLink().run()}
          disabled={!editor.isActive("link")}
        >
          <Unlink2 size={18} />
        </IconButton>

        <div className="mx-1 h-6 w-px bg-slate-200" />

        <IconButton
          label="서식초기화"
          title="서식 제거"
          onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
        >
          <Eraser size={18} />
        </IconButton>

        <div className="flex-1" />

        <IconButton
          label="실행취소"
          title="되돌리기"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().chain().focus().undo().run()}
        >
          <Undo2 size={18} />
        </IconButton>

        <IconButton
          label="다시실행"
          title="다시"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().chain().focus().redo().run()}
        >
          <Redo2 size={18} />
        </IconButton>
      </div>

      {/* 본문 */}
      <EditorContent editor={editor} />
    </div>
  );
}
